// Quick Stats Strip - Top metrics bar with sparklines for Macro Monitor
import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, RefreshCw } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { useIndicatorData } from '../../hooks/useIndicatorData';
import { Tooltip } from '../ui/Tooltip';

interface QuickStatProps {
  indicatorId: string;
  label: string;
  format?: 'number' | 'percent' | 'currency' | 'rating';
  decimals?: number;
  onClick?: (indicatorId: string) => void;
  isSelected?: boolean;
}

const formatValue = (
  value: number | null,
  format: string = 'number',
  decimals: number = 1
): string => {
  if (value === null || value === undefined) return 'N/A';

  switch (format) {
    case 'percent':
      return `${value.toFixed(decimals)}%`;
    case 'currency':
      if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
      if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
      return `$${value.toFixed(0)}`;
    case 'rating':
      return value.toFixed(0);
    default:
      if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
      if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
      if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
      return value.toFixed(decimals);
  }
};

const QuickStat: React.FC<QuickStatProps> = ({
  indicatorId,
  label,
  format = 'number',
  decimals = 1,
  onClick,
  isSelected = false,
}) => {
  const { data, statistics, yoyChange, isLoading, metadata } = useIndicatorData(indicatorId, 'UZB', 10);

  const latestValue = statistics?.latest ?? null;
  const latestChange = yoyChange.length > 0 ? yoyChange[yoyChange.length - 1]?.change : null;

  const isPositive = latestChange !== null && latestChange > 0;
  const isNegative = latestChange !== null && latestChange < 0;

  // Determine if positive is good based on metadata
  const goodDirection = metadata?.goodDirection ?? 'up';
  const isGood = goodDirection === 'up' ? isPositive : goodDirection === 'down' ? isNegative : true;
  const isBad = goodDirection === 'up' ? isNegative : goodDirection === 'down' ? isPositive : false;

  // Mini sparkline data
  const sparklineData = useMemo(() => {
    return data.slice(-8).map(d => ({ value: d.value ?? 0 }));
  }, [data]);

  const sparklineColor = isGood ? '#00d26a' : isBad ? '#ff3b3b' : '#6b7280';

  if (isLoading) {
    return (
      <div className="bg-terminal-panel px-3 py-2 flex flex-col justify-center min-w-[100px] animate-pulse">
        <div className="h-2 bg-gray-700 rounded w-12 mb-1" />
        <div className="h-4 bg-gray-700 rounded w-16" />
      </div>
    );
  }

  const tooltipContent = metadata
    ? `${metadata.name}: ${metadata.description}`
    : label;

  return (
    <Tooltip content={tooltipContent} position="bottom">
      <button
        onClick={() => onClick?.(indicatorId)}
        className={`bg-terminal-panel px-3 py-2 flex flex-col justify-center min-w-[100px] transition-colors hover:bg-terminal-bg/50 ${
          isSelected ? 'ring-1 ring-data-amber/50' : ''
        }`}
      >
        {/* Label */}
        <span className="text-[8px] text-gray-500 uppercase tracking-wider mb-0.5">
          {label}
        </span>

        {/* Value Row */}
        <div className="flex items-center gap-2">
          {/* Main Value */}
          <span className="text-sm font-semibold text-white">
            {formatValue(latestValue, format, decimals)}
          </span>

          {/* Change Indicator */}
          {latestChange !== null && (
            <div className={`flex items-center gap-0.5 ${isGood ? 'text-data-green' : isBad ? 'text-data-red' : 'text-gray-500'}`}>
              {isPositive ? (
                <TrendingUp size={10} />
              ) : isNegative ? (
                <TrendingDown size={10} />
              ) : (
                <Minus size={10} />
              )}
              <span className="text-[9px]">
                {latestChange > 0 ? '+' : ''}
                {latestChange.toFixed(1)}%
              </span>
            </div>
          )}
        </div>

        {/* Mini Sparkline */}
        {sparklineData.length > 3 && (
          <div className="h-3 mt-1 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparklineData}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={sparklineColor}
                  strokeWidth={1}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </button>
    </Tooltip>
  );
};

// Metric definition type
interface MetricConfig {
  id: string;
  label: string;
  format: 'number' | 'percent' | 'currency' | 'rating';
  decimals?: number;
}

// Default key metrics to display
// IMPORTANT: IDs must match WB_INDICATOR_CODES in types.ts
const DEFAULT_METRICS: MetricConfig[] = [
  { id: 'gdp', label: 'GDP', format: 'currency' },
  { id: 'gdp_growth', label: 'Growth', format: 'percent' },
  { id: 'inflation_cpi', label: 'Inflation', format: 'percent' },
  { id: 'fdi_inflows', label: 'FDI', format: 'currency' },
  { id: 'reserves', label: 'Reserves', format: 'currency' },
  { id: 'population', label: 'Population', format: 'number' },
  { id: 'trade_gdp', label: 'Trade/GDP', format: 'percent' },
  { id: 'external_debt_gdp', label: 'Debt/GDP', format: 'percent' },
];

interface QuickStatsStripProps {
  selectedIndicator?: string | null;
  onIndicatorSelect?: (indicatorId: string) => void;
  metrics?: MetricConfig[];
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const QuickStatsStrip: React.FC<QuickStatsStripProps> = ({
  selectedIndicator,
  onIndicatorSelect,
  metrics = DEFAULT_METRICS,
  onRefresh,
  isRefreshing = false,
}) => {
  return (
    <div className="flex items-stretch border-b border-terminal-border bg-terminal-header">
      {/* Metrics Grid */}
      <div className="flex-1 flex overflow-x-auto scrollbar-thin gap-px">
        {metrics.map(metric => (
          <QuickStat
            key={metric.id}
            indicatorId={metric.id}
            label={metric.label}
            format={metric.format}
            decimals={metric.decimals ?? 1}
            onClick={onIndicatorSelect}
            isSelected={selectedIndicator === metric.id}
          />
        ))}
      </div>

      {/* Refresh Button */}
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="px-3 flex items-center justify-center text-gray-500 hover:text-gray-300 transition-colors border-l border-terminal-border"
        >
          <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
        </button>
      )}
    </div>
  );
};

export default QuickStatsStrip;
